import { ClaimItem } from '~support-at-home/models';

/**
 * Late submission predicates (TAV-2592) - web mirror of `requiresLateSubmissionReason` in the API's
 * `claims.mjs`. Kept in lockstep with that function so the row indicator and the server's
 * `items_late_submission_required` count can never disagree.
 *
 * ONLY Services Australia raises this flag. There is no pre-flagging before a push (Cole,
 * 2026-08-14): only SA can apply the real rule (>60 days after the earlier of the care recipient's
 * departure date or the budget period end), and neither date is computable here.
 *
 * `is_late_submission` used to stand in before SA answered, derived from the service date alone. It
 * over-flagged so badly that on a catch-up claim every row was flagged, and because SA cleared each
 * row as it accepted it, a mixed claim read backwards - no flag where SA had answered, a flag on every
 * row it had not. The column is still populated but nothing reads it; a flag now means exactly one
 * thing, which is that Services Australia asked.
 */
export const requiresLateSubmissionReason = (item: ClaimItem): boolean => item.sa_late_submission_required === true;

/**
 * REACHABLE, not outstanding: true once a row is flagged as late (by either predicate) OR already
 * carries a captured reason. The column/row control must stay visible after a reason has been
 * saved, or the control the user just used disappears from under them (TAV-2591's paperclip
 * lesson) - `requiresLateSubmissionReason` alone would go false again as soon as the reason lands.
 */
export const isLateSubmissionReachable = (item: ClaimItem): boolean =>
  requiresLateSubmissionReason(item) || Boolean(item.late_submission_reason_code);
